import { useDriver } from "@/libs/auth/data/driver";
import { useChats } from "../hooks/useChats";
import { Link } from "react-router";
import { routePaths } from "@/App";
import { cn } from "@/utils/cn";
import { format } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "../utils";
import { LoadingSpinner } from "@/components/LoadingSpinner";

interface ChatSidebarProps {
  currentChatId?: string;
}

function ChatSidebarLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="h-full flex flex-col overflow-y-auto space-y-2 bg-list-background">
      <header className="border-b-2 px-4 py-3 flex items-center bg-white">
        <Link
          to={routePaths.home}
          className="flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          {t("common.back")}
        </Link>
      </header>
      {children}
    </div>
  );
}

export function ChatSidebar({ currentChatId }: ChatSidebarProps) {
  const { t } = useTranslation();
  const { data: chats, loading, error } = useChats();
  const { driver } = useDriver();

  if (loading) {
    return (
      <ChatSidebarLayout>
        <div className="w-full h-full grid place-items-center">
          <LoadingSpinner />
        </div>
      </ChatSidebarLayout>
    );
  }

  if (error) {
    return (
      <ChatSidebarLayout>
        <div className="w-full h-full grid place-items-center">
          <span className="text-destructive">
            {t("chat.errorLoadingChats")}
          </span>
        </div>
      </ChatSidebarLayout>
    );
  }

  if (!chats || chats.length === 0) {
    return (
      <ChatSidebarLayout>
        <div className="w-full h-full grid place-items-center">
          <span className="text-muted-foreground">{t("chat.noMessages")}</span>
        </div>
      </ChatSidebarLayout>
    );
  }

  return (
    <ChatSidebarLayout>
      <ul className="flex flex-col gap-2 flex-1 p-2">
        {chats.map((chat) => {
          const otherParticipant = chat.participants?.find(
            (participant) => participant.employeeId !== driver?.id,
          );
          const displayName = otherParticipant?.name || chat.name;

          return (
            <ChatLinkItem
              key={chat.id}
              id={chat.id}
              name={displayName}
              lastMessage={chat.lastMessage?.content}
              lastMessageTime={chat.lastMessage?.createdAt}
              profilePictureUrl={otherParticipant?.avatarUrl || null}
              unreadCount={chat.unreadCount}
              isActive={currentChatId === chat.id}
            />
          );
        })}
      </ul>
    </ChatSidebarLayout>
  );
}

interface ChatLinkItemProps {
  id: string;
  name: string;
  lastMessage?: string;
  lastMessageTime?: string;
  profilePictureUrl?: string | null;
  unreadCount?: number | undefined;
  isActive?: boolean;
}

function ChatLinkItem({
  id,
  lastMessage,
  lastMessageTime,
  name,
  unreadCount,
  profilePictureUrl,
  isActive,
}: ChatLinkItemProps) {
  return (
    <Link to={routePaths.createChatPath(id)}>
      <li
        className={cn(
          "flex gap-3 items-center p-3 transition-colors rounded-sm",
          {
            "bg-quaternary-medium": isActive,
            "hover:bg-gray-50": !isActive,
          },
        )}
      >
        <Avatar className="size-11 flex-shrink-0">
          <AvatarImage
            src={profilePictureUrl || undefined}
            alt={name || "User Avatar"}
          />
          <AvatarFallback className="text-gray-400 text-sm font-medium">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>

        <div className="w-full overflow-hidden space-y-0.5">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold truncate">{name}</h4>

            {lastMessageTime && (
              <span className="tabular-nums font-sans text-[14px] shrink-0 ml-2">
                {format(new Date(lastMessageTime), "HH:mm")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <p className="text-gray-500 truncate flex-1 min-w-0">
              {lastMessage || "Noch keine Nachrichten"}
            </p>

            {!!unreadCount && (
              <div className="shrink-0 bg-primary text-white tabular-nums font-sans text-[14px] rounded-full px-2 text-sm">
                {unreadCount}
              </div>
            )}
          </div>
        </div>
      </li>
    </Link>
  );
}
